import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowLeft, LuCheck, LuShieldCheck } from 'react-icons/lu';
import XiPageShell from '../components/xi/XiPageShell';
import useSessionUser from '../hooks/useSessionUser';
import { updateUserProfile } from '../components/api';
import { authFetch, authHeaders } from '../utils/authFetch';

const LANGUAGES = [
  ['en', 'English'],
  ['de', 'German'],
  ['fr', 'French'],
  ['es', 'Spanish'],
  ['nl', 'Dutch'],
  ['pt', 'Portuguese'],
  ['ru', 'Russian'],
  ['ja', 'Japanese'],
  ['hi', 'Hindi'],
  ['ta', 'Tamil'],
  ['ar', 'Arabic'],
  ['ur', 'Urdu'],
  ['zh', 'Chinese (Mandarin)'],
  ['it', 'Italian'],
  ['ko', 'Korean'],
];

function apiBase() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:8000';
  }
  return 'https://app.neurolonic.com/api';
}

/**
 * Dedicated profile editing route.
 * Uses the exact same contract as the legacy modal:
 *   GET /user/details?user_id=…   (read)
 *   PUT /user/{id}                (write, via updateUserProfile)
 * Nothing about the payload changed.
 */
export default function ProfileEdit() {
  const navigate = useNavigate();
  const { user } = useSessionUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [details, setDetails] = useState(null);

  const [form, setForm] = useState({
    display_name: '',
    age: '',
    gender: '',
    role: '',
    phone: '',
    job_title: '',
    preferred_language: '',
  });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const userId = localStorage.getItem('neuro_user_id');
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        const res = await authFetch(`${apiBase()}/user/details?user_id=${encodeURIComponent(userId)}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (cancelled) return;
        const d = data?.data || null;
        setDetails(d);
        setForm({
          display_name: d?.display_name || d?.user_name || localStorage.getItem('neuro_display_name') || '',
          age: d?.age ?? '',
          gender: d?.gender || '',
          role: d?.role || '',
          phone: d?.phone || '',
          job_title: d?.job_title || '',
          preferred_language: d?.preferred_language || '',
        });
      } catch {
        if (!cancelled) setError('Could not load your profile. You can still edit the fields below.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function set(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    const userId = localStorage.getItem('neuro_user_id');
    if (!userId) {
      setError('You need to be signed in to save changes.');
      return;
    }

    const updates = {};
    if (form.display_name.trim()) updates.display_name = form.display_name.trim();
    if (form.age !== '') updates.age = parseInt(form.age, 10);
    if (form.gender) updates.gender = form.gender;
    if (form.role.trim()) updates.role = form.role.trim();
    if (form.phone.trim()) updates.phone = form.phone.trim();
    if (form.job_title.trim()) updates.job_title = form.job_title.trim();
    if (form.preferred_language) updates.preferred_language = form.preferred_language;

    setSaving(true);
    try {
      const res = await updateUserProfile(userId, updates);
      if (res && res.status_code === 200) {
        if (updates.display_name) {
          localStorage.setItem('neuro_display_name', updates.display_name);
        }
        setSuccess('Profile updated successfully.');
        setDetails((prev) => ({ ...(prev || {}), ...updates }));
      } else {
        setError(res?.message || 'Failed to update profile.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <XiPageShell
      eyebrow="Account"
      title="Edit profile"
      lede="Update the details Xi uses to personalise your experience."
      actions={
        <Link to="/profile" className="xi-btn xi-btn--ghost" style={{ height: 42, fontSize: '0.88rem' }}>
          <LuArrowLeft aria-hidden="true" />
          Back to profile
        </Link>
      }
    >
      <section className="xi-card" style={{ padding: 'clamp(18px, 2.6vw, 30px)' }}>
        {loading ? (
          <p style={{ color: 'var(--x-ink-3)', fontSize: '0.9rem' }}>Loading your details…</p>
        ) : (
          <form className="xi-form" onSubmit={handleSubmit}>
            {!localStorage.getItem('neuro_user_id') && (
              <div className="xi-error" role="status">
                You are not signed in. <Link to="/login" style={{ textDecoration: 'underline' }}>Sign in</Link> to save changes.
              </div>
            )}

            <div className="xi-form__grid">
              <label className="xi-field">
                <span className="xi-field__label">Display name</span>
                <input
                  className="xi-input"
                  type="text"
                  value={form.display_name}
                  onChange={set('display_name')}
                  placeholder="How Xi should address you"
                />
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Age</span>
                <input
                  className="xi-input"
                  type="number"
                  min="1"
                  max="120"
                  value={form.age}
                  onChange={set('age')}
                  placeholder="Your age"
                />
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Gender</span>
                <select className="xi-select" value={form.gender} onChange={set('gender')}>
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="LGBTQ">LGBTQ</option>
                  <option value="Other">Other</option>
                </select>
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Role</span>
                <input
                  className="xi-input"
                  type="text"
                  value={form.role}
                  onChange={set('role')}
                  placeholder="Your role"
                />
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Phone number</span>
                <input
                  className="xi-input"
                  type="tel"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="Optional"
                />
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Job title</span>
                <input
                  className="xi-input"
                  type="text"
                  value={form.job_title}
                  onChange={set('job_title')}
                  placeholder="Optional"
                />
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Preferred language</span>
                <select className="xi-select" value={form.preferred_language} onChange={set('preferred_language')}>
                  <option value="">Select language</option>
                  {LANGUAGES.map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="xi-field">
                <span className="xi-field__label">Email / username</span>
                <input
                  className="xi-input"
                  type="text"
                  value={details?.user_name || user.email || ''}
                  readOnly
                  aria-readonly="true"
                  style={{ opacity: 0.7, cursor: 'not-allowed' }}
                />
              </label>
            </div>

            {error && <div className="xi-error" role="alert">{error}</div>}
            {success && (
              <div
                className="xi-error"
                role="status"
                style={{
                  borderColor: 'rgba(52, 211, 153, 0.35)',
                  background: 'rgba(52, 211, 153, 0.10)',
                  color: '#6ee7b7',
                }}
              >
                <LuCheck aria-hidden="true" style={{ width: 16, height: 16, flex: 'none', marginTop: 2 }} />
                {success}
              </div>
            )}

            <div className="xi-form__actions">
              <button type="submit" className="xi-btn xi-btn--primary" disabled={saving}>
                {saving ? <span className="xi-spinner" /> : <LuCheck aria-hidden="true" />}
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              <Link to="/reset-password" className="xi-btn xi-btn--ghost">
                <LuShieldCheck aria-hidden="true" />
                Reset password
              </Link>
              <button
                type="button"
                className="xi-btn xi-btn--soft"
                onClick={() => navigate('/profile')}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>
    </XiPageShell>
  );
}
